import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Cake, Download, FileText, Landmark, Plus, Users, WalletCards } from "lucide-react";
import { Toaster, toast } from "sonner";
import { PasswordModal } from "@/components/PasswordModal";
import { CategoryChart } from "@/components/CategoryChart";
import { AdminPanel } from "@/components/AdminPanel";
import { ReceiptModal } from "@/components/ReceiptModal";
import { InviteModal } from "@/components/InviteModal";
import { SignatureModal } from "@/components/SignatureModal";
import { EntryTable } from "@/components/EntryTable";
import { EntriesPage } from "@/components/EntriesPage";
import { TithersPage } from "@/components/TithersPage";
import { MembersPage } from "@/components/MembersPage";
import { ClosingPage } from "@/components/ClosingPage";
import { GoalsProgress } from "@/components/Goals";
import { ChurchRanking } from "@/components/ChurchRanking";
import { EntryModal } from "@/components/EntryModal";
import { ChartsGrid, Filters, MobileNav, Sidebar, StatsGrid } from "@/components/DashboardParts";
import { API, CHURCHES, downloadBlob, errorText } from "@/lib/constants";
import "@/App.css";

let refreshing = null;
let lastRefreshAt = 0;
axios.interceptors.request.use((cfg) => ({ ...cfg, _sentAt: Date.now() }));
axios.interceptors.response.use(null, async (error) => {
  const cfg = error.config;
  if (error.response?.status !== 401 || !cfg || cfg._retry || /auth\/(login|refresh|logout)/.test(cfg.url)) throw error;
  cfg._retry = true;
  if (cfg._sentAt >= lastRefreshAt) {
    refreshing = refreshing || axios.post(`${API}/auth/refresh`, {}, { withCredentials: true }).then(() => { lastRefreshAt = Date.now(); }).finally(() => { refreshing = null; });
    await refreshing;
  }
  return axios(cfg);
});

const NAV_ITEMS = [["dashboard", "Visão geral", Landmark], ["entries", "Lançamentos", WalletCards], ["tithers", "Dizimistas", Users], ["members", "Aniversariantes", Cake], ["closing", "Fechamento", FileText]];
const EMPTY_STATS = { entradas: 0, saidas: 0, saldo: 0, lancamentos: 0, by_church: [], by_category: [] };

function Login({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" }); const [error, setError] = useState("");
  const submit = async (e) => { e.preventDefault(); try { const { data } = await axios.post(`${API}/auth/login`, form, { withCredentials: true }); onLogin(data); } catch (err) { const d = err.response?.data?.detail; setError(typeof d === "string" ? d : "Verifique o e-mail e a senha informados."); } };
  return <main className="login-shell"><section className="login-art"><img data-testid="login-logo" className="login-logo" src="/logo-adsa.png" alt="ADSA Brasil" /><p className="eyebrow">ADSA BRASIL · CAMPO DE PARELHEIROS</p><h1 data-testid="login-app-title">Livro Caixa ADSA BRASIL Campo de Parelheiros</h1><p>Controle o livro caixa das igrejas do campo com simplicidade, segurança e visão consolidada.</p><div className="art-stats"><span>07 igrejas</span><span>01 visão</span></div></section><section className="login-panel"><div className="login-box"><p className="eyebrow">BEM-VINDO DE VOLTA</p><h2>Acesse seu painel</h2><p className="muted">Use seu e-mail e senha para continuar.</p><form onSubmit={submit}><label>E-mail<input data-testid="login-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></label><label>Senha<input data-testid="login-password-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></label>{error && <div data-testid="login-error" className="error">{error}</div>}<button data-testid="login-submit-button" className="primary-btn" type="submit">Entrar no painel <span>→</span></button></form><small>Usuários de igreja visualizam apenas seus próprios lançamentos.</small></div></section></main>;
}

const useDashboardData = (church, period, range) => {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [entries, setEntries] = useState([]);
  const qs = `period=${period}&church=${encodeURIComponent(church)}${period === "custom" ? `&start=${range.start}&end=${range.end}` : ""}`;
  const ready = period !== "custom" || Boolean(range.start && range.end && range.start <= range.end);
  const load = useCallback(async () => {
    if (!ready) return;
    const config = { withCredentials: true };
    const [s, e] = await Promise.all([axios.get(`${API}/dashboard?${qs}`, config), axios.get(`${API}/entries?${qs}`, config)]);
    setStats(s.data); setEntries(e.data);
  }, [qs, ready]);
  useEffect(() => { load(); }, [load]);
  return { stats, entries, qs, ready, load };
};

const useExpenseCategories = () => {
  const [categories, setCategories] = useState(["Outros"]);
  useEffect(() => { axios.get(`${API}/expense-categories`, { withCredentials: true }).then(r => setCategories(r.data)); }, []);
  return [categories, setCategories];
};

const useRefreshKey = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = useCallback(() => setRefreshKey(k => k + 1), []);
  useEffect(() => { window.addEventListener("goals-updated", bump); return () => window.removeEventListener("goals-updated", bump); }, [bump]);
  return [refreshKey, bump];
};

const Overview = ({ user, isAdmin, church, stats, entries, refreshKey, onEdit, onDelete, onReceipt, onViewAll }) => <>
  <StatsGrid stats={stats} balanceLabel={isAdmin && church === "Todas" ? "Saldo consolidado" : "Saldo"} />
  <ChartsGrid stats={stats} title={isAdmin ? "Por igreja" : user.church} />
  {isAdmin && church === "Todas" && <ChurchRanking refreshKey={refreshKey} />}
  <GoalsProgress church={church} isAdmin={isAdmin} refreshKey={refreshKey} />
  <CategoryChart data={stats.by_category} />
  <section className="panel table-panel"><div className="panel-heading"><div><p className="eyebrow">MOVIMENTAÇÕES RECENTES</p><h2>Últimos lançamentos</h2></div><button data-testid="view-all-entries-button" className="link-btn" onClick={onViewAll}>Ver todos ({entries.length}) →</button></div>
    <EntryTable entries={entries.slice(0, 8)} onEdit={onEdit} onDelete={onDelete} onReceipt={onReceipt} /></section>
</>;

function Dashboard({ user, onLogout }) {
  const isAdmin = user.role === "admin";
  const [church, setChurch] = useState(isAdmin ? "Todas" : user.church);
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState({ start: "", end: "" });
  const [view, setView] = useState("dashboard");
  const [modal, setModal] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [invite, setInvite] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [expenseCategories, setExpenseCategories] = useExpenseCategories();
  const [refreshKey, bump] = useRefreshKey();
  const { stats, entries, qs, ready, load } = useDashboardData(church, period, range);
  const refreshAll = () => { load(); bump(); };
  const removeEntry = async (e) => {
    if (!window.confirm(`Excluir o lançamento de ${e.person_name}?`)) return;
    try { await axios.delete(`${API}/entries/${e.id}`, { withCredentials: true }); toast.success("Lançamento excluído"); refreshAll(); }
    catch (err) { toast.error(errorText(err, "Não foi possível excluir.")); }
  };
  const exportExcel = async () => {
    if (!ready) return toast.error("Escolha as datas inicial e final");
    const response = await axios.get(`${API}/reports/export?${qs}`, { withCredentials: true, responseType: "blob" });
    downloadBlob(response.data, "livro-caixa.xlsx");
  };
  const navProps = { navItems: NAV_ITEMS, view, setView, isAdmin, onInvite: () => setInvite({}), onSignature: () => setDialog("signature"), onAdmin: () => setDialog("admin") };
  const rowActions = { onEdit: setModal, onDelete: removeEntry, onReceipt: setReceipt };
  const closeDialog = () => setDialog(null);
  return <div className="app-shell">
    <Sidebar user={user} {...navProps} onPassword={() => setDialog("password")} onLogout={onLogout} />
    <main className="content">
      <header className="topbar"><div className="mobile-brand"><img className="side-logo" src="/logo-adsa.png" alt="ADSA Brasil" /> Livro Caixa ADSA</div>
        <div><p className="eyebrow">{isAdmin ? "ADMINISTRAÇÃO GERAL" : "CONTROLE DA IGREJA"}</p><h1 data-testid="page-title">{NAV_ITEMS.find(n => n[0] === view)[1]}</h1></div>
        <div className="top-actions"><button data-testid="export-excel-button" className="secondary-btn" onClick={exportExcel}><Download size={17} /> Exportar Excel</button><button data-testid="new-entry-button" className="primary-btn compact" onClick={() => setModal({})}><Plus size={18} /> Novo lançamento</button></div></header>
      <MobileNav {...navProps} />
      <Filters user={user} isAdmin={isAdmin} showPeriod={view === "dashboard"} period={period} setPeriod={setPeriod} range={range} setRange={setRange} church={church} setChurch={setChurch} />
      {view === "dashboard" && <Overview user={user} isAdmin={isAdmin} church={church} stats={stats} entries={entries} refreshKey={refreshKey} onViewAll={() => setView("entries")} {...rowActions} />}
      {view === "entries" && <EntriesPage church={church} expenseCategories={expenseCategories} refreshKey={refreshKey} onBulkDeleted={refreshAll} {...rowActions} />}
      {view === "members" && <MembersPage user={user} church={church} churches={CHURCHES} />}
      {view === "closing" && <ClosingPage church={church} isAdmin={isAdmin} onImported={refreshAll} />}
      {view === "tithers" && <TithersPage church={church} onInvite={t => setInvite({ name: t.name, phone: t.phone })} />}
    </main>
    {modal && <EntryModal user={user} expenseCategories={expenseCategories} entry={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); refreshAll(); }} />}
    {dialog === "password" && <PasswordModal onClose={closeDialog} />}
    {dialog === "admin" && <AdminPanel churches={CHURCHES} categories={expenseCategories} onCategoriesChange={setExpenseCategories} onClose={closeDialog} />}
    {dialog === "signature" && <SignatureModal user={user} churches={CHURCHES} onClose={closeDialog} />}
    {receipt && <ReceiptModal entry={receipt} onClose={() => setReceipt(null)} />}
    {invite && <InviteModal initialName={invite.name} initialPhone={invite.phone} onClose={() => setInvite(null)} />}
    <Toaster position="top-right" richColors />
  </div>;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => { axios.get(`${API}/auth/me`, { withCredentials: true }).then(r => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecking(false)); }, []);
  const logout = async () => { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); setUser(false); };
  if (checking) return <div className="loading-screen">Carregando seu painel...</div>;
  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
